import { useEffect, useState } from "react";
import { useFieldArray, useWatch, type Control } from "react-hook-form";

import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import SubSectionHeading from "@/components/SubSectionHeading";
import Table, { TableFooter } from "@/components/Table";
import { ControlledTextField } from "@/components/TextField";

import { getActionPlanColumns, type ActionPlanRow } from "./_ActionPlanTableColumns";
import AutoFocusedPlanField from "./_AutoFocusedPlanField";
import CareerPlanEmptySection from "./_CareerPlanEmptySection";
import SortablePlanItems from "./_SortablePlanItems";
import { EMPTY_ACTION_ITEM, type CareerPlanDocumentFormValues } from "./careerPlanDocument";

const ACTION_PLAN_COLUMNS = getActionPlanColumns();

type ActionPlanRowsProps = {
  control: Control<CareerPlanDocumentFormValues>;
  emptyTitle: string;
  addLabel: string;
  readOnly: boolean;
  canManage: boolean;
  disabled?: boolean;
  onBeginEdit: () => void;
};

export default function ActionPlanRows({
  control,
  emptyTitle,
  addLabel,
  readOnly,
  canManage,
  disabled = false,
  onBeginEdit
}: ActionPlanRowsProps) {
  const { fields, append, move, remove } = useFieldArray({ control, name: "actionItems" });
  const actionItems = useWatch({ control, name: "actionItems" });
  const [focusRowIndex, setFocusRowIndex] = useState(0);
  const rows: ActionPlanRow[] = fields.map((field, index) => ({
    id: field.id,
    focusArea: actionItems?.[index]?.focusArea ?? "",
    recommendedActivity: actionItems?.[index]?.recommendedActivity ?? "",
    successMeasure: actionItems?.[index]?.successMeasure ?? "",
    timeframe: actionItems?.[index]?.timeframe ?? ""
  }));

  useEffect(() => {
    if (readOnly) setFocusRowIndex(0);
  }, [readOnly]);

  const addItem = () => {
    if (disabled) return;
    if (readOnly) onBeginEdit();
    setFocusRowIndex(fields.length);
    append(EMPTY_ACTION_ITEM);
  };

  const addButton = canManage ? (
    <Button
      type="button"
      variant="filledTonal"
      icon="add"
      onClick={addItem}
      disabled={disabled}
      testId="add-action-row"
    >
      {addLabel}
    </Button>
  ) : null;

  if (fields.length === 0) {
    return (
      <CareerPlanEmptySection
        section="actionPlan"
        title={emptyTitle}
        addLabel={addLabel}
        canManage={canManage}
        disabled={disabled}
        onAdd={addItem}
      />
    );
  }

  if (readOnly) {
    return (
      <div data-testid="career-plan-action-plan">
        <Table
          data={rows}
          columns={ACTION_PLAN_COLUMNS}
          loading={false}
          renderFooter={() => <TableFooter totalRowCount={rows.length} />}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="career-plan-action-plan">
      <SortablePlanItems
        fields={fields}
        onMove={move}
        onDragStart={() => setFocusRowIndex(-1)}
        dragTestId={(index) => `reorder-action-row-${index}`}
      >
        {(_field, index, dragHandle) => (
          <div
            className="flex flex-col gap-4 rounded-xl border border-sys-brand-outline-variant p-4"
            data-testid={`career-plan-action-row-${index}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {dragHandle}
                <SubSectionHeading>{`Row ${index + 1}`}</SubSectionHeading>
              </div>
              <IconButton
                name="delete"
                skipMargin
                ariaLabel={`Remove action row ${index + 1}`}
                title={`Remove action row ${index + 1}`}
                onClick={() => remove(index)}
                data-testid={`remove-action-row-${index}`}
              />
            </div>
            <ActionPlanRowFields control={control} index={index} autoFocus={index === focusRowIndex} />
          </div>
        )}
      </SortablePlanItems>
      {addButton && <div>{addButton}</div>}
    </div>
  );
}

function ActionPlanRowFields({
  control,
  index,
  autoFocus
}: {
  control: Control<CareerPlanDocumentFormValues>;
  index: number;
  autoFocus: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <AutoFocusedPlanField
        control={control}
        name={`actionItems.${index}.focusArea`}
        label="Area of Focus"
        type="textarea"
        rows={3}
        autoFocus={autoFocus}
      />
      <ControlledTextField
        control={control}
        name={`actionItems.${index}.recommendedActivity`}
        label="Recommended Activity"
        type="textarea"
        rows={3}
      />
      <ControlledTextField
        control={control}
        name={`actionItems.${index}.successMeasure`}
        label="Success Measure"
        type="textarea"
        rows={3}
      />
      <ControlledTextField
        control={control}
        name={`actionItems.${index}.timeframe`}
        label="Timeframe"
        type="textarea"
        rows={3}
      />
    </div>
  );
}
